import {
  QueryResult,
  MutationResult,
  ApolloError,
  OperationVariables,
  ServerError,
  LazyQueryResult,
} from '@apollo/client';
import { useEffect } from 'react';
import { errors } from './errors';
import { useToasts } from '../../graphql/toasts/useToasts';
import { ConfigContext } from '../../providers/ConfigContext';
import { signOut } from 'aws-amplify/auth';

export const DEFAULT_ERROR = 'Something went wrong, please try again';

export const useErrorHandler = <
  TData = any,
  TVariables extends OperationVariables = Record<string, any>,
>(
  queryResult:
    | QueryResult<TData, TVariables>
    | MutationResult<TData>
    | LazyQueryResult<TData, TVariables>,
) => {
  const { pushToast } = useToasts();
  const config = ConfigContext.useContainer();
  const { data, error, loading } = queryResult;

  useEffect(() => {
    if (error && !loading) {
      if (isUnauthorizedError(error)) {
        // Sign out from Cognito
        signOut()
          .then(() => {
            window.location.href = config.cognito.redirectSignOut || '/';
          })
          .catch(() => {
            // Fallback in case signOut fails
            window.location.href = config.cognito.cognitoLogoutUrl || '/';
          });
      }

      const errorMessage = Object.keys(errors).includes(error.message)
        ? errors[error.message]
        : error.message;
      pushToast({
        variables: { toast: { message: errorMessage || DEFAULT_ERROR } },
      });
    }
  }, [loading, error, pushToast, config]);

  return { data, error, loading };
};

function isUnauthorizedError(error: ApolloError): boolean {
  return (error.networkError as ServerError)?.statusCode === 401;
}
